import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../dialog';
import { Button } from '../button';
import { Checkbox } from '../checkbox';
import { Badge } from '../badge';
import { DataTable } from '../../DataTable';
import { toast } from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { User } from '@shiftly/shared/src/schema';
import { useBulkEmailInvoices } from '../../../lib/supabase/pay/supabasePayService';
import { formatCurrencyGBP } from '../../../utils/format';
import { Mail, Users, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../alert';
import { DateTime } from 'luxon';

interface DeactivatedUser {
  id: string;
  user_id: string;
  userName: string;
  userEmail: string;
  totalEarnings: number;
  deactivatedAt: DateTime;
}

interface BulkEmailDeactivatedUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  users: User[];
  pays: any[];
  month: number;
  year: number;
}

export function BulkEmailDeactivatedUsersDialog({
  isOpen,
  onClose,
  users,
  pays,
  month,
  year
}: BulkEmailDeactivatedUsersDialogProps) {
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isEmailing, setIsEmailing] = useState(false);
  const [isCheckingSubmissions, setIsCheckingSubmissions] = useState(true);
  const [emailResults, setEmailResults] = useState<{
    success: Array<{ user_id: string; userEmail: string; userName: string }>;
    failed: Array<{ user_id: string; userEmail: string; userName: string; error: string }>;
  }>({ success: [], failed: [] });

  // Filter to get deactivated users with earnings > 0 and unsubmitted invoices
  const [deactivatedUsersWithEarnings, setDeactivatedUsersWithEarnings] = useState<DeactivatedUser[]>([]);

  const { mutateAsync: bulkEmailInvoicesToUsers } = useBulkEmailInvoices();

  useEffect(() => {
    const fetchUnsubmittedUsers = async () => {
      setIsCheckingSubmissions(true);
      const deactivatedUsers: DeactivatedUser[] = [];

      if (users && pays) {
        for (const pay of pays) {
          const user = users.find(u => u.id === pay.invoice.user_id);
          if (user && user.deactivated_at && user.email) {
            const totalEarnings = pay.invoice.amount_gbp;
            if (totalEarnings > 0) {
              // Only include users with unsubmitted invoices
              if (pay.invoice.status != "submitted") {
                deactivatedUsers.push({
                  id: pay.invoice.id,
                  user_id: pay.invoice.user_id,
                  userName: user.full_name || 'Unknown User',
                  userEmail: user.email,
                  totalEarnings,
                  deactivatedAt: user.deactivated_at
                });
              }
            }
          }
        }
      }

      setDeactivatedUsersWithEarnings(deactivatedUsers.sort((a, b) => a.userName.localeCompare(b.userName)));
      setIsCheckingSubmissions(false);
    };

    if (isOpen) {
      fetchUnsubmittedUsers();
    }
  }, [users, pays, month, year, isOpen]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(deactivatedUsersWithEarnings.map(u => u.user_id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (user_id: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(user_id);
    } else {
      newSelected.delete(user_id);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkEmail = async () => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user to email');
      return;
    }

    setIsEmailing(true);
    const user_ids = Array.from(selectedUsers);
    const loadingId = toast.loading(`Emailing invoices to ${user_ids.length} users...`);

    const results: { 
      success: Array<{ user_id: string; userEmail: string; userName: string }>;
      failed: Array<{ user_id: string; userEmail: string; userName: string; error: string }>;
    } = { success: [], failed: [] };

    try {
      // Use bulk email function
      const bulkResult = await bulkEmailInvoicesToUsers({user_ids, year, month});
      
      if (bulkResult?.success) {
        // Process results
        bulkResult.results.forEach(result => {
          const user = deactivatedUsersWithEarnings.find(u => u.user_id === result.userId);
          const userName = user?.userName || 'Unknown User';
          
          if (result.success && result.userId) {
            results.success.push({
              user_id: result.userId,
              userEmail: result.userEmail,
              userName
            });
          } else {
            results.failed.push({
              user_id: result.userId,
              userEmail: result.userEmail,
              userName,
              error: result.error || 'Unknown error'
            });
          }
        });

        setEmailResults(results);

        if (results.success.length > 0) {
          toast.success(`Successfully emailed ${results.success.length} users`);
        }
        if (results.failed.length > 0) {
          toast.error(`Failed to email ${results.failed.length} users`);
        }
      } else {
        throw new Error('Bulk email operation failed');
      }

    } catch (error: any) {
      console.error('Error in bulk email:', error);
      toast.error('Failed to send emails: ' + error.message);
    } finally {
      setIsEmailing(false);
      toast.dismiss(loadingId);
    }
  };

  const columns: ColumnDef<DeactivatedUser>[] = [
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={selectedUsers.size === deactivatedUsersWithEarnings.length && deactivatedUsersWithEarnings.length > 0}
          onCheckedChange={(checked) => handleSelectAll(!!checked)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedUsers.has(row.original.user_id)}
          onCheckedChange={(checked) => handleSelectUser(row.original.user_id, !!checked)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'userName',
      header: 'Name',
    },
    {
      accessorKey: 'userEmail',
      header: 'Email',
    },
    {
      accessorKey: 'totalEarnings',
      header: 'Total Earnings',
      cell: ({ row }) => formatCurrencyGBP(row.original.totalEarnings),
    },
    {
      accessorKey: 'deactivatedAt',
      header: 'Deactivated',
      cell: ({ row }) => row.original.deactivatedAt.toLocaleString(),
    },
  ];

  const handleClose = () => {
    setSelectedUsers(new Set());
    setEmailResults({ success: [], failed: [] });
    setIsCheckingSubmissions(true);
    setDeactivatedUsersWithEarnings([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Email Invoices to Deactivated Users
          </DialogTitle>
          <DialogDescription>
            Send invoice emails to deactivated users who have unsubmitted invoices for this month.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {isCheckingSubmissions ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-3">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-border dark:border-brand-border mx-auto" />
                <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                  Checking submission status...
                </p>
              </div>
            </div>
          ) : deactivatedUsersWithEarnings.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center py-8">
              <div className="space-y-3">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
                <h3 className="text-lg font-medium text-brand-dark dark:text-foreground">
                  No deactivated users found
                </h3>
                <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                  There are no deactivated users with unsubmitted invoices for this month.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 py-4 border-b">
                <Badge variant="outline" className="text-sm">
                  {deactivatedUsersWithEarnings.length} users found
                </Badge>
                <Button
                  onClick={handleBulkEmail}
                  disabled={selectedUsers.size === 0 || isEmailing}
                  className="flex items-center gap-2"
                  size="sm"
                >
                  <Mail className="h-4 w-4" />
                  {isEmailing ? 'Sending...' : `Email Selected (${selectedUsers.size})`}
                </Button>
              </div>

              <div className="flex-1 overflow-auto">
                <DataTable
                  data={deactivatedUsersWithEarnings}
                  columns={columns}
                  searchPlaceholder="Search users..."
                  initialSorting={[{ id: 'userName', desc: false }]}
                  pageSize={6}
                  pageSizeOptions={[6]}
                />
              </div>
            </>
          )}
        </div>

        {/* Email Results */}
        {(emailResults.success.length > 0 || emailResults.failed.length > 0) && (
          <div className="space-y-2 pt-4">
            {emailResults.failed.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Failed to email ({emailResults.failed.length}):</strong>{' '}
                  {emailResults.failed.map(user => `${user.userName}: ${user.error}`).join(', ')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
} 